/**
 * @file CustomerChartWidget.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-02-02
 */

import React from 'react';
import useThemeColors from '../hooks/useThemeColors';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function CustomerChartWidget({ stats, loading }) {
  /*
   * Theme colours, re-read when the theme changes.
   *
   * This used to call getComputedStyle on every render, which sounds live and
   * is not: toggling the theme does not re-render this component, so the canvas
   * kept its old palette until something else happened to. useThemeColors
   * watches the attribute and updates state, which does.
   */
  const colors = useThemeColors({
    bgmain: '--bg-main',
    primary: '--color-primary',
    primaryRgb: '--color-primary-rgb',
    textPrimary: '--text-primary',
    textSecondary: '--text-secondary',
    borderPrimary: '--border-primary'
  });

  // After the hook, not before it: a hook behind an early return runs on some
  // renders and not others, which is the one thing hooks may not do.
  if (loading) return null;

  const chartData = {
    labels: stats?.monthlyData?.map(d => d.month) || [],
    datasets: [{
      label: 'New Customers',
      data: stats?.monthlyData?.map(d => d.count) || [],
      borderColor: colors.primary,
      backgroundColor: `rgba(${colors.primaryRgb}, 0.1)`,
      pointBackgroundColor: colors.primary,
      pointBorderColor: colors.bgmain,
      pointBorderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.3
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'New Customer Procurement',
        color: colors.textPrimary,
        font: { size: 12 },
        padding: { top: 5, bottom: 10 }
      }
    },
    scales: {
      x: {
        grid: { color: colors.borderPrimary },
        ticks: { color: colors.textSecondary, font: { size: 9 } }
      },
      y: {
        grid: { color: colors.borderPrimary },
        ticks: { color: colors.textSecondary, font: { size: 9 } }
      }
    }
  };

  return (
    <div className="dashboard-card chart-card-compact">
      <div className="card-body chart-body-compact">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}

export default CustomerChartWidget;
